/**
 * Centralized asset path constants
 * Organized by asset type for better maintainability
 *
 * Heavy content assets (models, textures, environment, audio, video, images,
 * downloadables) are NOT in the repo or the build output — they live in
 * Supabase Storage (public buckets `assets` and `downloadables`) behind
 * Supabase's CDN. Only small UI assets (/assets/ui), the Draco/KTX2 decoders
 * and photon.min.js are served from public/.
 *
 * The CDN origin defaults to the Supabase project (derived from
 * VITE_SUPABASE_URL, which is already required). Override with
 * VITE_ASSET_BASE_URL — e.g. a different CDN, or '' to serve from local
 * public/ copies during offline development.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export const ASSET_BASE_URL = (
  import.meta.env.VITE_ASSET_BASE_URL ??
  (SUPABASE_URL ? `${SUPABASE_URL.replace(/\/$/, '')}/storage/v1/object/public` : '')
).replace(/\/$/, '');

/**
 * Resolve an absolute asset path ('/assets/...' or '/downloadables/...') to
 * its CDN URL. Paths under /assets/ui are local-only — don't wrap those.
 */
export function assetUrl(path) {
  return `${ASSET_BASE_URL}${path}`;
}

// Base asset directories
const ASSETS_BASE = '/assets';

// Subdirectory constants (CDN-hosted except UI)
const MODELS = assetUrl(`${ASSETS_BASE}/models`);
const TEXTURES = assetUrl(`${ASSETS_BASE}/textures/materials`);
const ENVIRONMENT = assetUrl(`${ASSETS_BASE}/environment`);
const UI = `${ASSETS_BASE}/ui`; // local (public/assets/ui)
const AUDIO = assetUrl(`${ASSETS_BASE}/audio`);

// Material texture paths (KTX2/Basis GPU-compressed — see docs/asset-pipeline)
export const TEXTURE_PATHS = {
  GRASS: {
    BASE_COLOR: `${TEXTURES}/grass/basecolor.ktx2`,
    NORMAL: `${TEXTURES}/grass/normal.ktx2`,
    ROUGHNESS: `${TEXTURES}/grass/roughness.ktx2`,
    METALLIC: `${TEXTURES}/grass/metallic.ktx2`,
  },

  BRICK: {
    BASE_COLOR: `${TEXTURES}/brick/basecolor.ktx2`,
    NORMAL: `${TEXTURES}/brick/normal.ktx2`,
    ROUGHNESS: `${TEXTURES}/brick/roughness.ktx2`,
    METALLIC: `${TEXTURES}/brick/metallic.ktx2`,
  },

  RAMMED_EARTH: {
    BASE_COLOR: `${TEXTURES}/rammed-earth/basecolor.ktx2`,
    NORMAL: `${TEXTURES}/rammed-earth/normal.ktx2`,
    AO: `${TEXTURES}/rammed-earth/ao.ktx2`,
    ROUGHNESS: `${TEXTURES}/rammed-earth/roughness.ktx2`,
    METALLIC: `${TEXTURES}/rammed-earth/metallic.ktx2`,
    DISPLACEMENT: `${TEXTURES}/rammed-earth/displacement.ktx2`,
  },
};

// Model paths
export const MODEL_PATHS = {
  PROPS: {
    BOOMBOX: `${MODELS}/props/boombox.glb`,
    PLANTER: `${MODELS}/props/planter.glb`,
    TREE: `${MODELS}/props/tree_01.glb`,
    POD2: `${MODELS}/props/pod2.glb`,
  },
  CHARACTERS: {
    POD: `${MODELS}/characters/pod.glb`,
  },
  ENVIRONMENT: {
    FUTURISTIC_EXHIBITION: `${MODELS}/environment/FuturisticExhibition.glb`,
  },
};

// Environment paths (skyboxes, HDRI, etc.)
export const ENVIRONMENT_PATHS = {
  SKYBOX: {
    CLOUDY_MORNING: `${ENVIRONMENT}/skybox/cloudymorning4k.env`,
  },
  HDRI: {
    OUTDOOR_FIELD: `${ENVIRONMENT}/hdri/outdoor-field/hdr_2k.exr`,
    OUTDOOR_FIELD_PREVIEW: `${ENVIRONMENT}/hdri/outdoor-field/preview.jpg`,
  },
};

// UI asset paths (local — bundled in public/assets/ui)
export const UI_PATHS = {
  ICONS: {
    VIDEOCAM: `${UI}/icons/videocam.png`,
    VIDEOCAM_OFF: `${UI}/icons/videocam-off.png`,
    THUMBS_UP: `${UI}/icons/thumbs-up.png`,
  },
  AVATARS: {
    STANDBY: `${UI}/avatars/standby.jpg`,
  },
  PORTAL_PREVIEWS: {
    HUB: `${UI}/portal-previews/hub.webp`,
    EXHIBITION_HALL: `${UI}/portal-previews/exhibition-hall.webp`,
    BASKETBALL_COURT: `${UI}/portal-previews/basketball-court.webp`,
    SOCCER_PITCH: `${UI}/portal-previews/soccer-pitch.webp`,
  },
};

// Audio asset paths
export const AUDIO_PATHS = {
  MUSIC: {
    TEST: `${AUDIO}/music/test-track.mp3`,
    TEST_ORIGINAL: `${AUDIO}/music/test-track-original.mp3`,
  },
  SFX: {
    // Future sound effects
  },
};

// Helper function to validate asset paths
export function validateAssetPath(path) {
  if (!path || typeof path !== 'string') return false;
  return path.startsWith(ASSETS_BASE) || path.startsWith(`${ASSET_BASE_URL}${ASSETS_BASE}`);
}

// Helper function to get all texture paths for a material type
export function getTexturePaths(materialType) {
  return TEXTURE_PATHS[materialType.toUpperCase()] || {};
}

// Helper function to get model path
export function getModelPath(category, name) {
  const categoryPaths = MODEL_PATHS[category.toUpperCase()];
  return categoryPaths ? categoryPaths[name.toUpperCase()] : null;
}

// Helper function to get UI path
export function getUIPath(category, name) {
  const categoryPaths = UI_PATHS[category.toUpperCase()];
  return categoryPaths ? categoryPaths[name.toUpperCase()] : null;
}
